import { getSupabaseClient, isSupabaseConfigured } from './supabaseClient';

export interface EmailSubmissionResult {
  success: boolean;
  message: string;
}

/**
 * Generate a random confirmation token
 */
function generateConfirmationToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Call the Supabase Edge Function to send confirmation email
 */
async function sendConfirmationEmail(email: string, token: string): Promise<void> {
  try {
    const supabase = getSupabaseClient();
    if (!supabase || !isSupabaseConfigured()) {
      return;
    }

    // Get the Supabase URL from the client
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || import.meta.env.EXPO_PUBLIC_SUPABASE_URL;
    if (!supabaseUrl) {
      console.warn('Supabase URL not configured, cannot send confirmation email');
      return;
    }

    // Call the edge function
    const { data, error } = await supabase.functions.invoke('send-deals-confirmation-email', {
      body: {
        email: email.toLowerCase().trim(),
        confirmation_token: token,
      },
    });

    if (error) {
      console.error('Error calling confirmation email function:', error);
    } else {
      console.log('Confirmation email sent:', data);
    }
  } catch (error) {
    console.error('Error sending confirmation email:', error);
    // Don't throw - email sending failure shouldn't block signup
  }
}

/**
 * Submit email for exclusive deals list
 * @param email - Email address to subscribe
 * @returns Promise with success status and message
 */
export async function submitEmailForDeals(email: string): Promise<EmailSubmissionResult> {
  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return {
      success: false,
      message: 'Please enter a valid email address',
    };
  }

  try {
    const supabase = getSupabaseClient();
    
    if (supabase && isSupabaseConfigured()) {
      // Generate confirmation token
      const confirmationToken = generateConfirmationToken();
      const normalizedEmail = email.toLowerCase().trim();

      // Check if email already exists
      const { data: existing } = await supabase
        .from('deals_email_list')
        .select('id, confirmed_at')
        .eq('email', normalizedEmail)
        .single();

      if (existing) {
        // Email already exists
        if (existing.confirmed_at) {
          return {
            success: false,
            message: 'This email is already confirmed and subscribed.',
          };
        } else {
          // Update existing unconfirmed email with new token
          const { error: updateError } = await supabase
            .from('deals_email_list')
            .update({
              confirmation_token: confirmationToken,
              created_at: new Date().toISOString(), // Reset created_at
              email_sent_at: null, // Reset email_sent_at
            })
            .eq('email', normalizedEmail);

          if (updateError) {
            throw updateError;
          }

          // Send confirmation email
          await sendConfirmationEmail(normalizedEmail, confirmationToken);

          return {
            success: true,
            message: 'Confirmation email sent! Please check your inbox.',
          };
        }
      }

      // Insert new email with confirmation token
      const { error } = await supabase
        .from('deals_email_list')
        .insert({
          email: normalizedEmail,
          confirmation_token: confirmationToken,
          source: 'exclusive_deals_page',
          // created_at will be set automatically by default
        });

      if (error) {
        // Handle duplicate email error
        if (error.code === '23505') {
          return {
            success: false,
            message: 'This email is already subscribed.',
          };
        }

        // Other errors
        if (import.meta.env.DEV) {
          console.warn('Supabase insert failed:', error);
        }
        throw error;
      }

      // Send confirmation email
      await sendConfirmationEmail(normalizedEmail, confirmationToken);

      return {
        success: true,
        message: 'Successfully subscribed! Please check your email to confirm.',
      };
    }

    // Fallback: log to console in dev mode
    if (import.meta.env.DEV) {
      console.log('Email subscription (dev mode):', email);
    }

    // Return success even in fallback mode to provide good UX
    return {
      success: true,
      message: 'Successfully subscribed! You\'ll receive exclusive deals soon.',
    };
  } catch (error) {
    console.error('Error submitting email:', error);
    return {
      success: false,
      message: 'Something went wrong. Please try again later.',
    };
  }
}
